(() => {
    const STORAGE_KEY = 'recentlyViewedProducts';
    const MAX_STORED_PRODUCTS = 20;
    const SECTION_SELECTOR = '[data-recently-viewed-section]';
    const PRODUCT_PATH_PATTERN = /\/products\/([^/?#]+)/;

    const getStoredHandles = () => {
        try {
            const raw = window.localStorage.getItem(STORAGE_KEY);
            if (!raw) return [];

            const parsed = JSON.parse(raw);
            if (!Array.isArray(parsed)) return [];

            return parsed
                .map((item) => {
                    if (typeof item === 'string') return item;
                    if (item && typeof item.handle === 'string') return item.handle;
                    if (item && typeof item.url === 'string') {
                        const match = item.url.match(/\/products\/([^/?#]+)/);
                        return match ? decodeURIComponent(match[1]) : null;
                    }

                    return null;
                })
                .filter(Boolean);
        } catch (error) {
            return [];
        }
    };

    const storeHandles = (handles) => {
        try {
            window.localStorage.setItem(STORAGE_KEY, JSON.stringify(handles.slice(0, MAX_STORED_PRODUCTS)));
        } catch (error) {
            // Ignore storage write failures.
        }
    };

    const getCurrentProductHandle = () => {
        const match = window.location.pathname.match(PRODUCT_PATH_PATTERN);
        return match ? decodeURIComponent(match[1]) : null;
    };

    const trackCurrentProduct = () => {
        const handle = getCurrentProductHandle();
        if (!handle) return;

        const nextHandles = [handle, ...getStoredHandles().filter((item) => item !== handle)];
        storeHandles(nextHandles);
    };

    const getRootUrl = (section) => {
        if (section && section.dataset.rootUrl) return section.dataset.rootUrl;
        return '/';
    };

    const buildProductUrl = (handle, section) => {
        const rootUrl = getRootUrl(section).replace(/\/?$/, '/');
        return `${rootUrl}products/${encodeURIComponent(handle)}`;
    };

    const getCardSectionId = (section) => {
        return section.dataset.enableQuickAdd === 'false'
            ? 'recently-viewed-product-card-no-quick-add'
            : 'recently-viewed-product-card';
    };

    const fetchProductCard = async (handle, section) => {
        const response = await fetch(
            `${buildProductUrl(handle, section)}?section_id=${getCardSectionId(section)}`
        );

        if (!response.ok) {
            throw new Error(`Failed to load product card: ${handle}`);
        }

        const html = await response.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const card = parsed.querySelector('[data-recently-viewed-card]');

        return card ? card.outerHTML : '';
    };

    const destroySwiper = (sliderEl) => {
        if (sliderEl && sliderEl.swiper) {
            sliderEl.swiper.destroy(true, true);
        }
    };

    const initSwiper = (section) => {
        const sliderEl = section.querySelector('.featured-collection-showcase__slider');
        if (!sliderEl || typeof Swiper === 'undefined') return;

        destroySwiper(sliderEl);

        const footerEl = section.querySelector('.featured-collection-showcase__footer');
        const nextEl = section.querySelector('.featured-collection-showcase__nav-button--next');
        const prevEl = section.querySelector('.featured-collection-showcase__nav-button--prev');
        const scrollbarEl = section.querySelector('.featured-collection-showcase__scrollbar');
        const slideCount = sliderEl.querySelectorAll('.swiper-slide').length;
        const isRtl = document.documentElement.dir === 'rtl';

        sliderEl.setAttribute('dir', isRtl ? 'rtl' : 'ltr');

        if (footerEl) {
            footerEl.hidden = slideCount <= 1;
        }

        const swiper = new Swiper(sliderEl, {
            slidesPerView: 1.33,
            spaceBetween: 14,
            speed: 650,
            watchOverflow: true,
            grabCursor: true,
            breakpoints: {
                750: {
                    slidesPerView: 2.15,
                    spaceBetween: 18,
                },
                990: {
                    slidesPerView: 3.2,
                    spaceBetween: 22,
                },
                1280: {
                    slidesPerView: 4,
                    spaceBetween: 24,
                },
            },
            scrollbar:
                slideCount > 1 && scrollbarEl
                    ? {
                        el: scrollbarEl,
                        draggable: true,
                    }
                    : undefined,
            navigation:
                slideCount > 1 && nextEl && prevEl
                    ? {
                        nextEl,
                        prevEl,
                    }
                    : undefined,
            on: {
                init() {
                    if (typeof equalizeSwiperHeights === 'function') equalizeSwiperHeights(this.el);
                },
                resize() {
                    if (typeof equalizeSwiperHeights === 'function') equalizeSwiperHeights(this.el);
                },
                imagesReady() {
                    if (typeof equalizeSwiperHeights === 'function') equalizeSwiperHeights(this.el);
                },
            },
        });

        if (typeof swiper.changeLanguageDirection === 'function') {
            swiper.changeLanguageDirection(isRtl ? 'rtl' : 'ltr');
        }
    };

    const renderEmptyState = (section) => {
        const trackEl = section.querySelector('.featured-collection-showcase__track');
        const emptyEl = section.querySelector('.featured-collection-showcase__empty');
        const footerEl = section.querySelector('.featured-collection-showcase__footer');
        const sliderEl = section.querySelector('.featured-collection-showcase__slider');

        if (trackEl) {
            trackEl.innerHTML = '';
        }

        if (footerEl) {
            footerEl.hidden = true;
        }

        if (emptyEl) {
            emptyEl.hidden = false;
            emptyEl.textContent = section.dataset.emptyMessage || emptyEl.textContent;
        }

        destroySwiper(sliderEl);
    };

    const hideSection = (section) => {
        renderEmptyState(section);
        section.hidden = true;
    };

    const initSection = async (section) => {
        const trackEl = section.querySelector('.featured-collection-showcase__track');
        const emptyEl = section.querySelector('.featured-collection-showcase__empty');
        if (!trackEl) return;

        section.hidden = false;

        if (emptyEl) {
            emptyEl.hidden = true;
        }

        const limit = Number(section.dataset.limit || 8);
        const currentHandle = getCurrentProductHandle();
        const handles = getStoredHandles()
            .filter((handle) => handle && handle !== currentHandle)
            .slice(0, limit);

        if (!handles.length) {
            hideSection(section);
            return;
        }

        try {
            const cards = await Promise.all(
                handles.map((handle) =>
                    fetchProductCard(handle, section).catch(() => '')
                )
            );

            const visibleCards = cards
                .map((card) => card.trim())
                .filter(Boolean);

            if (!visibleCards.length) {
                hideSection(section);
                return;
            }

            section.hidden = false;
            trackEl.innerHTML = visibleCards.join('');
            initSwiper(section);
        } catch (error) {
            hideSection(section);
        }
    };

    const initSections = (root = document) => {
        root.querySelectorAll(SECTION_SELECTOR).forEach((section) => {
            initSection(section);
        });
    };

    const boot = () => {
        trackCurrentProduct();
        initSections();
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', boot, { once: true });
    } else {
        boot();
    }

    document.addEventListener('shopify:section:load', (event) => {
        const section = event.target && event.target.matches && event.target.matches(SECTION_SELECTOR)
            ? event.target
            : event.target && event.target.querySelector
                ? event.target.querySelector(SECTION_SELECTOR)
                : null;

        if (section) {
            initSection(section);
        }
    });
})();
